import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { Share2, Copy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DetectorScore = ({ name, score }) => {
  const radius = 30;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const isHuman = score < 15;
  const color = isHuman ? '#059669' : '#E11D48';

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="detector-score w-20 h-20">
        <svg width="80" height="80">
          <circle cx="40" cy="40" r={radius} stroke="#E4E4E7" strokeWidth="6" fill="none" />
          <circle cx="40" cy="40" r={radius} stroke={color} strokeWidth="6" fill="none" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
        </svg>
        <div className="detector-score-text text-sm font-bold">{score}%</div>
      </div>
      <span className="text-xs font-medium text-muted-foreground">{name}</span>
    </div>
  );
};

const Result = () => {
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResult = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/results/${resultId}`);
      setResult(response.data);
    } catch (error) {
      toast.error('Result not found');
    } finally {
      setLoading(false);
    }
  }, [resultId]);

  useEffect(() => {
    fetchResult();
  }, [fetchResult]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const shareResult = () => {
    const shareData = {
      title: 'Check out my humanized text - Penocio',
      text: 'I used Penocio to make my AI text undetectable!',
      url: window.location.href
    };

    if (navigator.share && navigator.canShare(shareData)) {
      navigator.share(shareData);
    } else {
      copyToClipboard(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold mb-4">Result not found</h2>
            <Link to="/"><Button>Go Home</Button></Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 py-12">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <Badge className="mb-4" variant="secondary">Shared Result</Badge>
            <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter mb-4">
              AI Text Successfully Humanized
            </h1>
            <p className="text-muted-foreground">Created with Penocio</p>
          </div>

          <Card className="p-6 md:p-8 border-zinc-200 mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading font-bold text-lg mb-4">Before (AI-Generated)</h3>
                <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md mb-4 max-h-[400px] overflow-y-auto">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{result.input_text}</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  {result.input_detectors.map((detector) => (
                    <DetectorScore key={detector.name} name={detector.name} score={detector.score} />
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg">After (Humanized)</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(result.output_text)}>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button size="sm" variant="outline" onClick={shareResult}>
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-md mb-4 max-h-[400px] overflow-y-auto">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{result.output_text}</p>
                </div>
                <div className="flex flex-wrap gap-4">
                  {result.output_detectors.map((detector) => (
                    <DetectorScore key={detector.name} name={detector.name} score={detector.score} />
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="outline">{result.style}</Badge>
                <span className="text-sm text-muted-foreground font-mono">{result.word_count} words</span>
              </div>
            </div>
          </Card>

          <Card className="p-8 text-center bg-secondary border-zinc-200">
            <h2 className="text-2xl font-heading font-bold mb-4">Create Your Own</h2>
            <p className="text-muted-foreground mb-6">Try Penocio for free and make your AI text undetectable</p>
            <Link to="/">
              <Button size="lg">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Result;
